import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { api } from '../../services/api';
import type { ContactSubmission } from '../../types/api';

export const AdminContactPage: React.FC = () => {
    const { setFlashMessage } = useOutletContext<{ setFlashMessage: (msg: any) => void }>();
    const [contacts, setContacts] = useState<ContactSubmission[]>([]);
    const [statusFilter, setStatusFilter] = useState<'all' | 'unread' | 'read' | 'archived'>('all');
    const [page, setPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [loading, setLoading] = useState<boolean>(true);
    const [selectedMessage, setSelectedMessage] = useState<ContactSubmission | null>(null);

    const loadContacts = async () => {
        setLoading(true);
        try {
            const filter = statusFilter === 'all' ? undefined : statusFilter;
            const res = await api.adminGetContacts(filter, page);
            setContacts(res.data);
            if (res.meta) {
                setTotalPages(res.meta.last_page);
            }
        } catch {
            // error handled
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadContacts();
    }, [statusFilter, page]);

    const handleStatusUpdate = async (id: number, status: 'unread' | 'read' | 'archived') => {
        try {
            await api.adminUpdateContactStatus(id, status);
            setFlashMessage({ type: 'success', text: `Message marked as ${status}.` });
            loadContacts();
            if (selectedMessage && selectedMessage.id === id) {
                setSelectedMessage({ ...selectedMessage, status });
            }
        } catch (err: any) {
            setFlashMessage({ type: 'error', text: err.message || 'Failed to update status.' });
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm('Delete this message permanently?')) return;
        try {
            await api.adminDeleteContact(id);
            setFlashMessage({ type: 'success', text: 'Message deleted.' });
            loadContacts();
            if (selectedMessage && selectedMessage.id === id) {
                setSelectedMessage(null);
            }
        } catch (err: any) {
            setFlashMessage({ type: 'error', text: err.message || 'Failed to delete message.' });
        }
    };

    const openMessage = (item: ContactSubmission) => {
        setSelectedMessage(item);
        if (item.status === 'unread') {
            handleStatusUpdate(item.id, 'read');
        }
    };

    return (
        <div className="space-y-8">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-extrabold text-white tracking-tight">Contact Inbox</h1>
                    <p className="text-sm text-slate-400 mt-1">Review incoming partnership inquiries, youth workshop invites, and messages.</p>
                </div>

                <div className="flex items-center gap-2">
                    {(['all', 'unread', 'read', 'archived'] as const).map(tab => (
                        <button
                            key={tab}
                            type="button"
                            onClick={() => {
                                setStatusFilter(tab);
                                setPage(1);
                            }}
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize cursor-pointer ${statusFilter === tab ? 'bg-emerald-600 text-white' : 'bg-slate-900 text-slate-400 hover:text-white'}`}
                        >
                            {tab}
                        </button>
                    ))}
                </div>
            </div>

            {/* Modal to view full message */}
            {selectedMessage && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
                    <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 max-w-lg w-full space-y-6 shadow-2xl">
                        <div className="flex items-center justify-between">
                            <span className={`px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${selectedMessage.status === 'unread' ? 'bg-rose-500/10 text-rose-400 border border-rose-500/20' : 'bg-slate-800 text-slate-400'}`}>
                                {selectedMessage.status}
                            </span>
                            <button 
                                type="button" 
                                onClick={() => setSelectedMessage(null)}
                                className="text-slate-400 hover:text-white text-xl"
                            >
                                &times;
                            </button>
                        </div>

                        <div className="space-y-2">
                            <h2 className="text-xl font-bold text-white">{selectedMessage.subject}</h2>
                            <p className="text-xs text-slate-400">
                                From: <span className="text-white font-semibold">{selectedMessage.name}</span> &lt;{selectedMessage.email}&gt;
                            </p>
                            <p className="text-[11px] text-slate-500 font-mono">
                                Received on: {new Date(selectedMessage.created_at).toLocaleString()}
                            </p>
                        </div>

                        <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800 text-slate-300 text-sm leading-relaxed whitespace-pre-wrap max-h-60 overflow-y-auto">
                            {selectedMessage.message}
                        </div>

                        <div className="flex items-center justify-between pt-4 border-t border-slate-800">
                            <a 
                                href={`mailto:${selectedMessage.email}?subject=${encodeURIComponent('Re: ' + selectedMessage.subject)}`}
                                className="px-4 py-2 rounded-xl text-xs font-bold bg-emerald-600 hover:bg-emerald-500 text-white"
                            >
                                Reply via Email &rarr;
                            </a>
                            <div className="flex items-center gap-2">
                                <button 
                                    type="button" 
                                    onClick={() => handleStatusUpdate(selectedMessage.id, selectedMessage.status === 'archived' ? 'read' : 'archived')}
                                    className="px-3 py-2 rounded-xl text-xs font-semibold text-slate-400 hover:text-white bg-slate-800"
                                >
                                    {selectedMessage.status === 'archived' ? 'Unarchive' : 'Archive'}
                                </button>
                                <button 
                                    type="button" 
                                    onClick={() => handleDelete(selectedMessage.id)}
                                    className="px-3 py-2 rounded-xl text-xs font-semibold text-rose-400 hover:text-rose-300 bg-rose-950/40"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Submissions Table */}
            <div className="p-6 rounded-3xl glass-card overflow-x-auto">
                <table className="w-full text-left text-sm">
                    <thead>
                        <tr className="border-b border-slate-800 text-xs text-slate-400 uppercase tracking-wider">
                            <th className="py-4 font-semibold">Sender</th>
                            <th className="py-4 font-semibold">Subject</th>
                            <th className="py-4 font-semibold">Date</th>
                            <th className="py-4 font-semibold">Status</th>
                            <th className="py-4 font-semibold text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800">
                        {contacts.length > 0 ? (
                            contacts.map(item => (
                                <tr key={item.id} className="hover:bg-slate-800/20">
                                    <td className="py-4">
                                        <span className="font-bold text-white block">{item.name}</span>
                                        <span className="text-xs text-slate-400">{item.email}</span>
                                    </td>
                                    <td className="py-4">
                                        <button 
                                            type="button" 
                                            onClick={() => openMessage(item)}
                                            className="font-medium text-white hover:text-emerald-400 text-left cursor-pointer"
                                        >
                                            {item.subject}
                                        </button>
                                    </td>
                                    <td className="py-4 text-xs text-slate-400">
                                        {new Date(item.created_at).toLocaleDateString()}
                                    </td>
                                    <td className="py-4">
                                        <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold border ${item.status === 'unread' ? 'bg-rose-500/10 text-rose-400 border-rose-500/20' : 'bg-slate-800 text-slate-400 border-slate-700'}`}>
                                            {item.status}
                                        </span>
                                    </td>
                                    <td className="py-4 text-right space-x-2">
                                        <button 
                                            type="button" 
                                            onClick={() => openMessage(item)}
                                            className="px-2.5 py-1 rounded text-xs bg-slate-900 border border-slate-700 text-slate-300 hover:text-white cursor-pointer"
                                        >
                                            Read
                                        </button>
                                        <button 
                                            type="button" 
                                            onClick={() => handleDelete(item.id)}
                                            className="px-2.5 py-1 rounded text-xs bg-rose-950/60 text-rose-400 border border-rose-800/40 hover:bg-rose-900 cursor-pointer"
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={5} className="py-8 text-center text-slate-500">
                                    {loading ? 'Loading inbox...' : 'No inquiries in this folder.'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>

                {totalPages > 1 && (
                    <div className="mt-6 flex items-center justify-end gap-2 text-xs">
                        <button
                            type="button"
                            disabled={page <= 1}
                            onClick={() => setPage(page - 1)}
                            className="px-3 py-1.5 rounded bg-slate-900 border border-slate-800 text-slate-400 disabled:opacity-40"
                        >
                            Previous
                        </button>
                        <span className="text-slate-400">Page {page} of {totalPages}</span>
                        <button
                            type="button"
                            disabled={page >= totalPages}
                            onClick={() => setPage(page + 1)}
                            className="px-3 py-1.5 rounded bg-slate-900 border border-slate-800 text-slate-400 disabled:opacity-40"
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
